import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

const notificationsFormSchema = z.object({
  emergency_alerts_mdm: z.boolean(),
  emergency_area: z.string(),
});

export type NotificationsFormValues = z.infer<typeof notificationsFormSchema>;

export const useNotificationsForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const initialValues: NotificationsFormValues = {
    emergency_alerts_mdm:
      !!settings['ribbonPlugin.settings']?.emergency_alerts_mdm,
    emergency_area: settings['ribbonPlugin.settings']?.emergency_area ?? '',
  };

  const form = useForm<NotificationsFormValues>({
    resolver: zodResolver(notificationsFormSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: NotificationsFormValues) => {
    setIsSubmitting(true);

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        emergency_alerts: data.emergency_alerts_mdm, // backwards compatibility
        emergency_alerts_mdm: data.emergency_alerts_mdm,
        emergency_area: data.emergency_area === '' ? null : data.emergency_area,
      },
    };

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-notifications-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsNotificationSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-messages-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsNotificationFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    initialValues,
  };
};
